import React, {
  useCallback,
  useContext, useEffect, useMemo, useState,
} from 'react';
import {
  Badge, Col, Modal, Row,
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { LoadingContext } from 'context/Loading';
import handlePromise from 'utils/Promise';
import ProfileService from 'services/api/Profile';
import ProjectService from 'services/api/Projects';
import ProcessService from 'services/api/Process';
import {
  AdminCreateProcessForm,
  ProcessEmailRecipient,
  PROCESS_OPERATION_TYPE_OPTIONS,
  ProcessOperationType,
  RepartoCapitalSubtype,
} from 'types/Process';
import { processExcelTemplate, ProcessExcelResult } from 'utils/processExcelTemplate';
import { buildEmailsFromContacts, ProfileContact } from 'utils/processContacts';
import Step1Form from './steps/Step1Form';
import Step2Result from './steps/Step2Result';
import Step3Upload from './steps/Step3Upload';
import Step4Logs from './steps/Step4Logs';
import Step5Contacts, { Step5FormValues } from './steps/Step5Contacts';
import Step6Result from './steps/Step6Result';

type ProcessRunErrorItem = { message?: string, accountName?: string };

type ProcessRunErrorBody = {
  message?: string,
  errors?: ProcessRunErrorItem[],
};

function formatProcessRunError(
  errorBody: ProcessRunErrorBody | null | undefined,
  t: ( key: string ) => string,
): string {
  if ( !errorBody ) return '';
  const { message: mainMessage, errors: errorsArray } = errorBody;
  if ( Array.isArray( errorsArray ) && errorsArray.length > 0 ) {
    return errorsArray
      .map( ( item ) => {
        const translated = t( `admin.processes.runErrors.${item.message ?? 'UNKNOWN'}` );
        return item.accountName ? `${translated} (${item.accountName})` : translated;
      } )
      .join( '\n' );
  }
  return mainMessage ?? '';
}

type CreateProcessModalProps = {
  onClose: () => void,
  reloadData: () => void,
};

type FormResult = {
  success: boolean,
  message?: string,
};

function CreateProcessModal( { onClose, reloadData }: CreateProcessModalProps ) {
  const { t } = useTranslation();
  const { toggleLoading } = useContext( LoadingContext );

  const [currentStep, setCurrentStep] = useState( 1 );
  const [formResult, setFormResult] = useState<FormResult | undefined>( undefined );
  const [activeProjects, setActiveProjects] = useState<any[] | undefined>( undefined );
  const [processLogs, setProcessLogs] = useState<ProcessExcelResult | null>( null );
  const [selectedPromotionId, setSelectedPromotionId] = useState<string | undefined>( undefined );
  const [selectedOperationType, setSelectedOperationType] = useState<string | undefined>(
    undefined,
  );
  const [selectedRepartoCapitalSubtype, setSelectedRepartoCapitalSubtype] = useState(
    undefined as RepartoCapitalSubtype | undefined,
  );
  const [submitResult, setSubmitResult] = useState<FormResult | undefined>( undefined );
  const [step2ExcelBlob, setStep2ExcelBlob] = useState<Blob | null>( null );
  const [step1Draft, setStep1Draft] = useState<Partial<AdminCreateProcessForm>>( {} );
  const [step3Draft, setStep3Draft] = useState<{ uploadFile?: File }>( {} );
  const [step5Draft, setStep5Draft] = useState<Partial<Step5FormValues>>( {} );

  const getProjects = useCallback( async () => {
    const [, response, projects] = await handlePromise(
      ProjectService.getProjectsList(),
    );
    if ( response?.ok ) {
      const list = projects?.data ?? [];
      const sorted = [...list].sort( ( a, b ) => String( a?.codigoPromocion ?? '' ).localeCompare(
        String( b?.codigoPromocion ?? '' ),
        'es',
        { sensitivity: 'base' },
      ) );
      setActiveProjects( sorted );
    } else {
      setActiveProjects( [] );
    }
  }, [] );

  useEffect( () => {
    getProjects();
  }, [getProjects] );

  const handleStep1ValuesChange = useCallback( ( values: AdminCreateProcessForm ) => {
    setStep1Draft( values );
  }, [] );

  const handleStep3ValuesChange = useCallback( ( values: { uploadFile?: File } ) => {
    setStep3Draft( values );
  }, [] );

  const handleStep5ValuesChange = useCallback( ( values: Step5FormValues ) => {
    setStep5Draft( values );
  }, [] );

  const submitStep1 = useCallback( async ( formValues: AdminCreateProcessForm ) => {
    toggleLoading( true );

    const dataToSend = {
      operationType: formValues.operationType,
      promotionId: formValues.promotionId,
      investmentAccounts: activeProjects?.find(
        ( item ) => item.promocionId === formValues.promotionId,
      )?.cuentasInversionIds,
    };

    const [errors, response, responseData] = await handlePromise(
      ProcessService.createAdminProcess( dataToSend, { responseType: 'blob' } ),
    );
    toggleLoading( false );

    if ( !response?.ok ) {
      setFormResult( { success: false, message: errors?.toString() ?? 'Error' } );
      setCurrentStep( 2 );
      return;
    }
    if ( !responseData ) {
      setFormResult( { success: false, message: 'No file' } );
      setCurrentStep( 2 );
      return;
    }

    const blob = responseData as Blob;
    setStep2ExcelBlob( blob );

    setFormResult( { success: true } );
    setSelectedPromotionId( formValues.promotionId );
    setSelectedOperationType( formValues.operationType );
    setSelectedRepartoCapitalSubtype(
      formValues.operationType === ProcessOperationType.REPARTO_CAPITAL
        ? ( formValues.repartoCapitalSubtype as RepartoCapitalSubtype )
        : undefined,
    );
    setCurrentStep( 2 );
    reloadData();
  }, [activeProjects, reloadData, toggleLoading] );

  const SKIP_EXCEL_VALIDATION = false; // TODO: revert - desactivar validación temporal

  const submitStep3 = useCallback( async ( formValues: { uploadFile?: File } ) => {
    const file = formValues.uploadFile;
    if ( !file ) return;
    toggleLoading( true );
    try {
      let result = await processExcelTemplate( file, {
        operationType: selectedOperationType ?? '',
      } );
      if ( SKIP_EXCEL_VALIDATION ) {
        result = {
          ...result,
          hasErrors: false,
          rowLogs: result.rowLogs.map( ( r ) => ( { ...r, success: true, errors: [] } ) ),
        };
      }
      setProcessLogs( result );
      setCurrentStep( 4 );
    } catch ( err ) {
      setProcessLogs( {
        rowLogs: [
          {
            accountName: 'Error',
            success: false,
            errors: [String( err )],
            rowIndex: 0,
          },
        ],
        hasErrors: true,
        dataToSend: [],
      } );
      setCurrentStep( 4 );
    } finally {
      toggleLoading( false );
    }
  }, [selectedOperationType, toggleLoading] );

  const goToStep3 = useCallback( () => {
    setCurrentStep( 3 );
  }, [] );

  const submitStep4 = useCallback( () => {
    setCurrentStep( 5 );
  }, [] );

  const handleStep4Restart = useCallback( () => {
    setProcessLogs( null );
    setCurrentStep( 3 );
  }, [] );

  const handleClose = useCallback( () => {
    setCurrentStep( 1 );
    setFormResult( undefined );
    setProcessLogs( null );
    setStep2ExcelBlob( null );
    setSelectedPromotionId( undefined );
    setSelectedOperationType( undefined );
    setSelectedRepartoCapitalSubtype( undefined );
    setSubmitResult( undefined );
    setStep1Draft( {} );
    setStep3Draft( {} );
    setStep5Draft( {} );
    onClose();
  }, [onClose] );

  const handleDownloadStep2Excel = useCallback( () => {
    if ( !step2ExcelBlob ) return;
    const ts = ( new Date() ).valueOf();
    const opSlug = selectedOperationType
      ? selectedOperationType.toLowerCase().replace( /_/g, '-' )
      : undefined;
    const baseName = opSlug ? `plantilla-${opSlug}-${ts}` : `plantilla-${ts}`;
    const url = URL.createObjectURL( step2ExcelBlob );
    const link = document.createElement( 'a' );
    link.href = url;
    link.download = `${baseName}.xlsx`;
    link.click();
    URL.revokeObjectURL( url );
  }, [selectedOperationType, step2ExcelBlob] );

  const submitStep5 = useCallback( async ( values: Step5FormValues ) => {
    toggleLoading( true );

    const toContactTypes = values.toContactTypes ?? [];
    const ccContactTypes = values.ccContactTypes ?? [];

    const [profileErrors, profileResponse, profileData] = await handlePromise(
      ProfileService.getAllByCuentaDeInversionBackOffice( { promocionId: selectedPromotionId } ),
    );

    if ( !profileResponse?.ok ) {
      toggleLoading( false );
      setSubmitResult( {
        success: false,
        message: profileErrors?.toString() ?? 'Error al cargar contactos de la promoción',
      } );
      setCurrentStep( 6 );
      return;
    }

    const contacts = profileData.data?.length ? profileData.data : [] as ProfileContact[];
    const toEmails = buildEmailsFromContacts( contacts, toContactTypes );
    const ccEmailsFromContacts = buildEmailsFromContacts( contacts, ccContactTypes );
    const ccEmailsManual = ( values.ccEmailsManual ?? [] ).map(
      ( email ) => ( { email, accountId: 0 } as ProcessEmailRecipient ),
    );
    const ccEmails = [...ccEmailsFromContacts, ...ccEmailsManual];

    const dataToSend = {
      operationType: selectedOperationType as string,
      promotionId: processLogs?.promotionId as string,
      data: processLogs?.dataToSend ?? [],
      ...( selectedOperationType === ProcessOperationType.REPARTO_CAPITAL
        && selectedRepartoCapitalSubtype
        ? { repartoCapitalSubtype: selectedRepartoCapitalSubtype }
        : {} ),
      emailSender: values.emailSender,
      toEmails,
      ccEmails,
      emailSubject: values.emailSubject,
      emailBody: values.emailBody,
      pdfRenderer: 'html' as const,
    };

    const [errors, response] = await handlePromise(
      ProcessService.runAdminProcess( dataToSend ),
    );
    toggleLoading( false );

    if ( !response?.ok ) {
      const errorBody = response?.data as ProcessRunErrorBody | undefined;
      const formattedMessage = formatProcessRunError( errorBody, t );
      setSubmitResult( {
        success: false,
        message: formattedMessage || ( errors?.toString() ?? 'Error' ),
      } );
      setCurrentStep( 6 );
      return;
    }
    reloadData();
    setSubmitResult( { success: true } );
    setCurrentStep( 6 );
  }, [
    processLogs?.dataToSend,
    processLogs?.promotionId,
    selectedOperationType,
    selectedRepartoCapitalSubtype,
    selectedPromotionId,
    toggleLoading,
    reloadData,
    t,
  ] );

  const step4OperationTypeLabel = useMemo( () => (
    selectedOperationType
      ? t( PROCESS_OPERATION_TYPE_OPTIONS.find(
        ( o ) => o.value === selectedOperationType,
      )?.labelKey ?? `admin.processes.type.${selectedOperationType}` )
      : undefined
  ), [selectedOperationType, t] );

  const step4PromotionName = useMemo( () => (
    activeProjects?.find( ( p ) => p.promocionId === selectedPromotionId )?.codigoPromocion
      ?? selectedPromotionId
  ), [activeProjects, selectedPromotionId] );

  const stepLabels = [
    t( 'admin.processes.wizardStep1' ),
    t( 'admin.processes.wizardStep2' ),
    t( 'admin.processes.wizardStep3' ),
    t( 'admin.processes.wizardStep4' ),
    t( 'admin.processes.wizardStep5' ),
    t( 'admin.processes.wizardStep6' ),
  ];

  return (
    <Modal
      show
      centered
      fullscreen={currentStep === 5 ? true : undefined}
      size={currentStep === 5 ? undefined : 'lg'}
      onHide={handleClose}
      backdrop="static"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title className="w-100">
          <h3 className="text-center">
            { t( 'admin.processes.createProcess' ) }
          </h3>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={11} className="mx-auto">
            <div className="d-flex gap-1 mb-5 justify-content-center">
              {stepLabels.map( ( label, index ) => {
                const stepNumber = index + 1;
                const isResultStep = currentStep === 6;
                const canNavigate = !isResultStep && stepNumber < currentStep;
                return (
                  <Badge
                    pill
                    bg={stepNumber === currentStep ? 'primary' : 'secondary'}
                    key={label}
                    role={canNavigate ? 'button' : undefined}
                    style={canNavigate ? { cursor: 'pointer' } : undefined}
                    onClick={canNavigate
                      ? () => setCurrentStep( stepNumber )
                      : undefined}
                  >
                    {stepNumber}
                    .
                    {' '}
                    {label}
                  </Badge>
                );
              } )}
            </div>
          </Col>
        </Row>
        <Row>
          <Col md={currentStep === 5 ? 11 : 10} className="mx-auto">
            {currentStep === 1 && (
              <Step1Form
                activeProjects={activeProjects}
                onSubmit={submitStep1}
                initialValues={step1Draft}
                onValuesChange={handleStep1ValuesChange}
              />
            )}

            {currentStep === 2 && formResult != null && (
              <Step2Result
                formResult={formResult}
                onNext={goToStep3}
                onDownloadExcel={handleDownloadStep2Excel}
                hasExcelDownload={!!step2ExcelBlob}
                onRestart={() => {
                  setFormResult( undefined );
                  setStep2ExcelBlob( null );
                  setStep1Draft( {} );
                  setStep3Draft( {} );
                  setStep5Draft( {} );
                  setSelectedRepartoCapitalSubtype( undefined );
                  setCurrentStep( 1 );
                }}
              />
            )}

            {currentStep === 3 && (
              <Step3Upload
                onSubmit={submitStep3}
                initialValues={step3Draft}
                onValuesChange={handleStep3ValuesChange}
              />
            )}

            {currentStep === 4 && (
              <Step4Logs
                processLogs={processLogs}
                onRestart={handleStep4Restart}
                onNext={submitStep4}
                operationTypeLabel={step4OperationTypeLabel}
                promotionName={step4PromotionName}
              />
            )}

            {currentStep === 5 && (
              <Step5Contacts
                onSubmit={submitStep5}
                initialValues={step5Draft}
                onValuesChange={handleStep5ValuesChange}
              />
            )}

            {currentStep === 6 && submitResult != null && (
              <Step6Result
                submitResult={submitResult}
                onClose={handleClose}
                onRestart={() => {
                  setSubmitResult( undefined );
                  setCurrentStep( 5 );
                }}
              />
            )}
          </Col>
        </Row>
      </Modal.Body>
    </Modal>
  );
}

export default CreateProcessModal;
